import { Loader } from '@googlemaps/js-api-loader'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import Alpine from 'alpinejs'

import { leaseSvg, leaseSvgHover, parkingSvg } from '../map/markerIcons'
import { cutsomControls } from '../map/controls'
import { parseSvgString } from '../map/parseSvgString'
import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_KEY } from '../map/constants'

// Desktop view
const isDesktopView = window.matchMedia('(min-width: 1124px').matches

const loader = new Loader({
  apiKey: GOOGLE_MAPS_KEY,
  version: 'weekly',
  libraries: ['marker'],
})

type location = google.maps.LatLngLiteral & { id?: string }

type Props = {
  centerLocation: location
  leaseLocations: location[]
  parkingLocations: google.maps.LatLngLiteral[]
  mapElement: HTMLElement
  extraMapOptions?: google.maps.MapOptions
}

function findOcc(arr, key) {
  let arr2: any = []

  arr.forEach((x) => {
    // Checking if there is any object in arr2
    // which contains the key value
    if (
      arr2.some((val) => {
        return val[key] == x[key]
      })
    ) {
      // If yes! then increase the count by 1
      arr2.forEach((k) => {
        if (k[key] === x[key]) {
          k['count']++
        }
      })
    } else {
      // If not! Then create a new object initialize
      // it with the present iteration key's value and
      // set the count to 1
      let a = {}
      a[key] = x[key]
      a['lat'] = x['lat']
      a['lng'] = x['lng']
      a['count'] = 1
      arr2.push(a)
    }
  })

  return arr2
}

// Create markers with all the events.
const createMarkers = (locations, map) => {
  const uniqueLocations = findOcc(locations, 'id')

  return uniqueLocations.map(({ lat, lng, id, count }) => {
    const marker =
      count > 1
        ? createAdvancedMarker(map, lat, lng, parseSvgString(leaseSvg))
        : createAdvancedMarker(map, lat, lng, parseSvgString(leaseSvg))

    addClick(marker, id)
    addMouseEnter(marker)

    document.querySelector(`[data-id="${id}"]`)?.addEventListener('mouseenter', () => {
      setMarkerContent(marker, parseSvgString(leaseSvgHover))
      startBouncingMarker(marker)
    })

    document.querySelector(`[data-id="${id}"]`)?.addEventListener('mouseleave', () => {
      setMarkerContent(marker, parseSvgString(leaseSvg))
      stopBouncingMarker(marker)
    })

    return marker
  })
}

function createAdvancedMarker(
  map: google.maps.Map,
  lat: number,
  lng: number,
  markerContent: HTMLElement
) {
  // Create the marker.
  return new google.maps.marker.AdvancedMarkerElement({
    position: { lat: lat, lng: lng },
    map: map,
    content: markerContent,
    title: '',
  })
}

function setMarkerContent(marker: AdvancedMarkerElement, htmlContent) {
  marker.content = htmlContent
}

function startBouncingMarker(marker: AdvancedMarkerElement) {
  const content = marker.content as HTMLElement
  content.classList.add('bounce')

  const time = 2 + Math.random() // 2s delay for easy to see the animation
  content.style.setProperty('--delay-time', time + 's')
}

function stopBouncingMarker(marker: AdvancedMarkerElement) {
  const content = marker.content as HTMLElement
  content.classList.remove('bounce')
}

function addClick(marker: AdvancedMarkerElement, id: String) {
  marker.addListener('click', () => {
    setTimeout(() => {
      !isDesktopView && Alpine.store('LeasesList').open()
    }, 1)

    // Highlight teaser in list when clicking on the marker.
    setTimeout(() => {
      document.querySelectorAll(`[data-id="${id}"]`).forEach((elem) => {
        elem.classList.add('after:translate-x-0')
      })

      setTimeout(() => {
        document.querySelectorAll(`[data-id="${id}"]`).forEach((elem) => {
          elem.classList.remove('after:translate-x-0')
        })
      }, 5000)

      const selector = isDesktopView
        ? `#desktop-search div[data-id="${id}"]`
        : `#mobile-search div[data-id="${id}"]`
      setTimeout(() => {
        ;(document.querySelector(selector) as HTMLElement)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }, 100)
    }, 300)
  })
}

function addMouseEnter(marker: AdvancedMarkerElement) {
  marker.content?.addEventListener('mouseenter', () => {
    marker.content = parseSvgString(leaseSvgHover)
    addMouseLeave(marker)
  })
}

function addMouseLeave(marker: AdvancedMarkerElement) {
  marker.content?.addEventListener('mouseleave', () => {
    marker.content = parseSvgString(leaseSvg)
    addMouseEnter(marker)
  })
}

// Add markers to the map and update markers when leaseLocations changes.
const addLeaseSearchMarkers = (
  map: google.maps.Map,
  locations: location[],
  centerLocation: location
) => {
  let leaseMarkers
  if (locations.length > 0) {
    // Add lease marker to the map
    leaseMarkers = createMarkers(locations, map)

    var bounds = new google.maps.LatLngBounds()
    //  Go through each...
    for (var i = 0, LtLgLen = leaseMarkers.length; i < LtLgLen; i++) {
      //  And increase the bounds to take this point
      bounds.extend(leaseMarkers[i].position)
    }
    //  Fit these bounds to the map
    map.fitBounds(bounds, 10)
  }

  if (locations.length == 0) {
    map.setCenter(centerLocation)
    map.setZoom(12)
  }

  const renderer = {
    render({ count, position }) {
      const cluster = document.createElement('div')
      cluster.className = 'mapCluster'
      cluster.textContent = String(count)

      return new google.maps.marker.AdvancedMarkerElement({
        position,
        content: cluster,
        zIndex: 10,
      })
    },
  }

  //@ts-ignore
  let markerCluster = new MarkerClusterer({ map, markers: leaseMarkers, renderer })

  if (window.markers) {
    window.markers.clearMarkers()
  }

  window.markers = markerCluster
}

const initLeaseSearchMap = ({
  centerLocation,
  leaseLocations,
  parkingLocations,
  mapElement,
  extraMapOptions,
}: Props): Promise<void> =>
  loader
    .importLibrary('marker')
    .then(({ AdvancedMarkerElement }) => {
      const mapOptions = {
        disableDefaultUI: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_TOP,
        },
        minZoom: 3,
        zoom: 12,
        center: centerLocation,
        gestureHandling: 'greedy',
        mapId: GOOGLE_MAPS_ID,
        v: 'beta',
        ...extraMapOptions,
      } as google.maps.MapOptions

      let leaseSearchMap = new google.maps.Map(mapElement, mapOptions)
      window.leaseSearchMap = leaseSearchMap

      // Add custom controls.
      cutsomControls(leaseSearchMap)

      leaseLocations.length > 0 &&
        addLeaseSearchMarkers(leaseSearchMap, leaseLocations, centerLocation)

      // Add parking markers to the map
      parkingLocations.map((position) => {
        const parkingMarker = new AdvancedMarkerElement({
          map: leaseSearchMap,
          position,
          title: 'parking',
          content: parseSvgString(parkingSvg),
          zIndex: 1,
        })
      })
    })
    .catch((err) => console.error(err))

window.initLeaseSearchMap = initLeaseSearchMap
window.addLeaseSearchMarkers = addLeaseSearchMarkers
